/**
 * Canvas renderer for the "lab" landing scene (design 5a).
 *
 * A day-cycle sky (night → dawn → day → golden hour → dusk → night) plus three
 * glowing "beings" — ASR, TTS, SLM — that split out of one intelligence orb in
 * the hero, take focus beside each model card, exchange data along pipeline
 * edges, and collapse back into the orb at the convergence section.
 *
 * All choreography keyframes are derived per-frame from the measured positions
 * of the registered scene anchors, so sections can move or new ones can be
 * inserted without editing this file. Coordinates are CSS pixels — the caller
 * applies the devicePixelRatio transform.
 */

export interface SceneLabels {
  asr: string;
  tts: string;
  slm: string;
  intelligence: string;
  sentUser: string;
  sentAssistant: string;
}

export interface SceneAnchorRect {
  top: number; // document-space offsetTop
  pin: number; // how long the section's sticky card stays pinned (height - viewport)
}

export interface SceneFrameInput {
  t: number; // seconds since start
  scrollY: number;
  w: number; // viewport width (CSS px)
  h: number; // viewport height (CSS px)
  docMax: number; // max scrollY
  navH: number;
  compact: boolean; // < md breakpoint: cards are full-width, orbs sit above them
  anchors: Partial<Record<'asr' | 'tts' | 'slm' | 'conv', SceneAnchorRect>>;
  labels: SceneLabels;
  eyeDist: number; // accumulated cursor travel — stirs the cloud orb
  rtl: boolean; // RTL documents flip the DOM layout — the choreography mirrors to match
}

const ORBS = [
  { col: [110, 185, 245], kind: 0 }, // ASR — listener
  { col: [245, 175, 95], kind: 1 }, // TTS — voice
  { col: [190, 150, 250], kind: 2 }, // SLM — mind
] as const;

// Deterministic starfield (same distribution as the design).
// Every 7th star is "bright": drawn with a glow halo and a 4-point sparkle.
const STARS = Array.from({ length: 90 }, (_, i) => ({
  x: ((i * 137.508) % 360) / 360,
  y: (((i * 71.3) % 360) / 360) * 0.72,
  s: 0.6 + ((i * 13) % 10) / 9,
  ph: i * 0.7,
  bright: i % 7 === 0,
  warm: i % 5 === 0, // subtle color variation: some stars lean warm instead of blue-white
}));

const fract = (v: number) => v - Math.floor(v);

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const ease = (t: number) => (t < 0 ? 0 : t > 1 ? 1 : t * t * (3 - 2 * t));

/** Piecewise-linear interpolation over scroll-keyed frames: [S, ...values] */
const seg = (frames: number[][], S: number): number[] => {
  if (S <= frames[0][0]) return frames[0].slice(1);
  for (let i = 0; i < frames.length - 1; i++) {
    const A = frames[i];
    const B = frames[i + 1];
    if (S <= B[0]) {
      const t = (S - A[0]) / (B[0] - A[0] || 1);
      return A.slice(1).map((v, k) => lerp(v, B[k + 1], t));
    }
  }
  return frames[frames.length - 1].slice(1);
};

const rgba = (col: readonly number[], a: number) =>
  'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',' + a.toFixed(3) + ')';

export function drawScene(ctx: CanvasRenderingContext2D, f: SceneFrameInput): void {
  const { t: T, scrollY: S, w, h, navH, compact, labels } = f;
  const max = Math.max(1, f.docMax);
  // Scale for absolute px radii/offsets tuned on the 1280px design frame
  const k = compact ? 1 : Math.min(1, Math.max(0.7, w / 1280));
  // DOM content sits in a centered max-width container on wide screens; orb X
  // coordinates map into the same container so the being/card relationship
  // stays consistent at any viewport width (identity below 1280px). RTL
  // documents flip the flex layout, so the choreography mirrors with them.
  const containerW = Math.min(w, 1280);
  const cx = (fx: number) => (w - containerW) / 2 + (f.rtl ? 1 - fx : fx) * containerW;

  // Missing anchors (page variants) park their beat beyond the scroll range
  const far = max * 2;
  const aT = f.anchors.asr?.top ?? far;
  const tT = f.anchors.tts?.top ?? far;
  const sT = f.anchors.slm?.top ?? far;
  const cT = f.anchors.conv?.top ?? far;
  const pinA = f.anchors.asr?.pin ?? 0;
  const pinT = f.anchors.tts?.pin ?? 0;
  const pinS = f.anchors.slm?.pin ?? 0;

  ctx.clearRect(0, 0, w, h);

  // --- sky palette: [S, topRGB, midRGB, horizonRGB, starAlpha, sunAltitude] ---
  const NIGHT = [6, 10, 24, 12, 20, 48, 26, 37, 71, 1, 0];
  const stops: number[][] = [[0, ...NIGHT]];
  if (f.anchors.asr) stops.push([aT - navH, 26, 35, 64, 74, 74, 120, 226, 144, 106, 0.35, 0.16]);
  if (f.anchors.tts) stops.push([tT - navH, 90, 154, 200, 164, 198, 228, 230, 221, 196, 0, 0.85]);
  if (f.anchors.slm) stops.push([sT - navH, 58, 48, 84, 168, 96, 56, 232, 177, 60, 0.15, 0.22]);
  if (f.anchors.conv) stops.push([cT - navH, 16, 20, 44, 42, 36, 80, 90, 66, 96, 0.7, 0]);
  stops.push([max, ...NIGHT]);
  const P = seg(stops, S);
  const rgbAt = (i: number) => 'rgb(' + (P[i] | 0) + ',' + (P[i + 1] | 0) + ',' + (P[i + 2] | 0) + ')';
  const starA = P[9];
  const sunAlt = P[10];

  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, rgbAt(0));
  g.addColorStop(0.55, rgbAt(3));
  g.addColorStop(1, rgbAt(6));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  // --- stars (no moon — only the three models read as objects) ---
  if (starA > 0.02) {
    for (const st of STARS) {
      const tw = 0.55 + 0.45 * Math.sin(T * 1.4 + st.ph);
      const a = starA * tw;
      const px = st.x * w;
      const py = st.y * h;
      const col = st.warm ? '255,244,230' : '230,238,250';
      if (st.bright) {
        // glow halo
        const gr = st.s * 6;
        const sg = ctx.createRadialGradient(px, py, 0, px, py, gr);
        sg.addColorStop(0, 'rgba(' + col + ',' + (a * 0.5).toFixed(3) + ')');
        sg.addColorStop(1, 'rgba(' + col + ',0)');
        ctx.fillStyle = sg;
        ctx.beginPath();
        ctx.arc(px, py, gr, 0, Math.PI * 2);
        ctx.fill();
        // 4-point sparkle that breathes with the twinkle
        const sl = st.s * (3 + tw * 2.5);
        ctx.strokeStyle = 'rgba(' + col + ',' + (a * 0.65).toFixed(3) + ')';
        ctx.lineWidth = 0.7;
        ctx.beginPath();
        ctx.moveTo(px - sl, py);
        ctx.lineTo(px + sl, py);
        ctx.moveTo(px, py - sl);
        ctx.lineTo(px, py + sl);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.arc(px, py, st.s, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(' + col + ',' + (a * (st.bright ? 0.95 : 0.8)).toFixed(3) + ')';
      ctx.fill();
    }

    // shooting stars: two staggered lanes, a brief streak every ~9s / ~14s.
    // Deterministic hash from the cycle index picks start point and length.
    for (let lane = 0; lane < 2; lane++) {
      const period = lane === 0 ? 9 : 14;
      const tt = T / period + lane * 0.53;
      const cyc = Math.floor(tt);
      const p = tt - cyc;
      const life = 0.09; // streak lives for ~9% of the period
      if (p > life) continue;
      const q = p / life;
      const h1 = fract(Math.sin(cyc * 127.1 + lane * 311.7) * 43758.5453);
      const h2 = fract(Math.sin(cyc * 269.5 + lane * 183.3) * 28001.8384);
      const sx0 = (0.2 + h1 * 0.7) * w;
      const sy0 = (0.04 + h2 * 0.22) * h;
      const ang = Math.PI * (0.72 + h2 * 0.12); // heading down-left
      const dx = Math.cos(ang); // negative: leftward
      const dy = Math.sin(ang); // positive: downward (canvas y grows down)
      const travel = (0.22 + h1 * 0.14) * w;
      const hx = sx0 + dx * q * travel;
      const hy = sy0 + dy * q * travel;
      const len = (90 + h2 * 80) * k;
      const fade = Math.sin(q * Math.PI) * starA;
      if (fade < 0.02) continue;
      const tail = ctx.createLinearGradient(hx, hy, hx - dx * len, hy - dy * len);
      tail.addColorStop(0, 'rgba(235,242,255,' + (fade * 0.9).toFixed(3) + ')');
      tail.addColorStop(0.3, 'rgba(210,225,250,' + (fade * 0.35).toFixed(3) + ')');
      tail.addColorStop(1, 'rgba(210,225,250,0)');
      ctx.strokeStyle = tail;
      ctx.lineWidth = 1.6;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(hx, hy);
      ctx.lineTo(hx - dx * len, hy - dy * len);
      ctx.stroke();
      ctx.lineCap = 'butt';
      // bright head with a small glow
      const hg2 = ctx.createRadialGradient(hx, hy, 0, hx, hy, 7);
      hg2.addColorStop(0, 'rgba(255,255,255,' + (fade * 0.95).toFixed(3) + ')');
      hg2.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = hg2;
      ctx.beginPath();
      ctx.arc(hx, hy, 7, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // --- daylight: a soft glow low on the horizon, not a distinct disc ---
  const horizonY = h * 0.8;
  if (sunAlt > 0.02) {
    const sx = w * 0.6;
    const sy = horizonY - sunAlt * h * 0.28;
    const warm = 1 - sunAlt;
    const sr = 320 * k;
    const sg = ctx.createRadialGradient(sx, sy, 0, sx, sy, sr);
    sg.addColorStop(
      0,
      'rgba(255,' + ((232 - warm * 60) | 0) + ',' + ((185 - warm * 95) | 0) + ',' + (0.34 + warm * 0.14).toFixed(3) + ')'
    );
    sg.addColorStop(1, 'rgba(255,200,140,0)');
    ctx.fillStyle = sg;
    ctx.beginPath();
    ctx.arc(sx, sy, sr, 0, Math.PI * 2);
    ctx.fill();
  }

  // --- rolling hills ---
  ctx.beginPath();
  ctx.moveTo(0, h);
  for (let x = 0; x <= w; x += 10) ctx.lineTo(x, h * 0.84 + Math.sin(x * 0.0032 + 1.4) * 22 + Math.sin(x * 0.008) * 8);
  ctx.lineTo(w, h);
  ctx.closePath();
  ctx.fillStyle = 'rgba(7,9,20,0.55)';
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(0, h);
  for (let x = 0; x <= w; x += 10) ctx.lineTo(x, h * 0.9 + Math.sin(x * 0.0026 + 4.2) * 18);
  ctx.lineTo(w, h);
  ctx.closePath();
  ctx.fillStyle = 'rgba(5,7,16,0.8)';
  ctx.fill();

  // --- orb choreography: keyframes [S, x, y, r, focus] per being ---
  const F = compact
    ? [
        seg(
          [
            [0, 0.6, 0.24, 26, 0],
            [aT - 750, 0.6, 0.24, 30, 0],
            [aT - navH, 0.5, 0.27, 78, 1],
            [aT + pinA, 0.5, 0.27, 78, 1],
            [tT - navH, 0.16, 0.1, 16, 0],
            [cT - navH, 0.16, 0.1, 16, 0],
            [cT + 80, 0.36, 0.8, 30, 0.7],
          ],
          S
        ),
        seg(
          [
            [0, 0.84, 0.32, 26, 0],
            [aT - 400, 0.5, 0.08, 16, 0],
            [tT - 750, 0.5, 0.08, 16, 0],
            [tT - navH, 0.5, 0.27, 78, 1],
            [tT + pinT, 0.5, 0.27, 78, 1],
            [sT - navH, 0.5, 0.08, 16, 0],
            [cT - navH, 0.5, 0.08, 16, 0],
            [cT + 80, 0.64, 0.8, 30, 0.7],
          ],
          S
        ),
        seg(
          [
            [0, 0.72, 0.46, 26, 0],
            [aT - 400, 0.84, 0.1, 16, 0],
            [sT - 750, 0.84, 0.1, 16, 0],
            [sT - navH, 0.5, 0.27, 78, 1],
            [sT + pinS, 0.5, 0.27, 78, 1],
            [cT - navH, 0.84, 0.1, 16, 0],
            [cT + 80, 0.5, 0.85, 30, 0.7],
          ],
          S
        ),
      ]
    : [
        seg(
          [
            [0, 0.68, 0.3, 32, 0],
            [aT - 750, 0.68, 0.3, 38, 0],
            [aT - navH, 0.7, 0.44, 112, 1],
            [aT + pinA, 0.7, 0.44, 112, 1],
            [tT - navH, 0.18, 0.13, 20, 0],
            [cT - navH, 0.18, 0.13, 20, 0],
            [cT + 80, 0.43, 0.79, 38, 0.7],
          ],
          S
        ),
        seg(
          [
            [0, 0.88, 0.4, 32, 0],
            [aT - 400, 0.5, 0.11, 20, 0],
            [tT - 750, 0.5, 0.11, 20, 0],
            [tT - navH, 0.3, 0.44, 112, 1],
            [tT + pinT, 0.3, 0.44, 112, 1],
            [sT - navH, 0.5, 0.11, 20, 0],
            [cT - navH, 0.5, 0.11, 20, 0],
            [cT + 80, 0.57, 0.79, 38, 0.7],
          ],
          S
        ),
        seg(
          [
            [0, 0.76, 0.58, 32, 0],
            [aT - 400, 0.82, 0.13, 20, 0],
            [sT - 750, 0.82, 0.13, 20, 0],
            [sT - navH, 0.7, 0.44, 112, 1],
            [sT + pinS, 0.7, 0.44, 112, 1],
            [cT - navH, 0.82, 0.13, 20, 0],
            [cT + 80, 0.5, 0.84, 38, 0.7],
          ],
          S
        ),
      ];
  const pts = F.map((fr) => ({ x: cx(fr[0]), y: fr[1] * h, r: fr[2] * k, fo: fr[3] }));

  // --- reusable cloud-orb body (hero skin + bottom re-merge) ---
  const drawCloudBody = (mx2: number, my2: number, R2: number, a2: number) => {
    ctx.save();
    ctx.globalAlpha = a2;
    ctx.beginPath();
    ctx.arc(mx2, my2, R2, 0, Math.PI * 2);
    ctx.clip();
    const lg2 = ctx.createLinearGradient(mx2 - R2 * 0.3, my2 - R2, mx2 + R2 * 0.2, my2 + R2);
    lg2.addColorStop(0, '#9aa3ea');
    lg2.addColorStop(0.5, '#cdd3f3');
    lg2.addColorStop(1, '#f6f7fc');
    ctx.fillStyle = lg2;
    ctx.fillRect(mx2 - R2, my2 - R2, R2 * 2, R2 * 2);
    const ph2 = T * 0.12 + f.eyeDist / 380;
    ORBS.forEach((o, i) => {
      let dd = (((ph2 - i) % 3) + 3) % 3;
      dd = Math.min(dd, 3 - dd);
      const boost = Math.max(0, 1 - dd);
      const tx2 = mx2 + Math.cos(T * 0.05 + i * 2.094) * R2 * 0.5;
      const ty2 = my2 + Math.sin(T * 0.06 + i * 2.094) * R2 * 0.45;
      const rr2 = R2 * (0.9 + boost * 0.18);
      const tg = ctx.createRadialGradient(tx2, ty2, 0, tx2, ty2, rr2);
      tg.addColorStop(0, rgba(o.col, 0.24 + boost * 0.34));
      tg.addColorStop(1, rgba(o.col, 0));
      ctx.fillStyle = tg;
      ctx.beginPath();
      ctx.arc(tx2, ty2, rr2, 0, Math.PI * 2);
      ctx.fill();
    });
    for (let kk = 0; kk < 6; kk++) {
      const cx2 = mx2 + Math.sin(T * 0.07 + kk * 1.9) * R2 * 0.55;
      const cy2 = my2 + R2 * (0.1 + (kk % 3) * 0.28) + Math.cos(T * 0.09 + kk) * 6;
      const cr = R2 * (0.45 + (kk % 2) * 0.25);
      const cg = ctx.createRadialGradient(cx2, cy2, 0, cx2, cy2, cr);
      cg.addColorStop(0, 'rgba(255,255,255,0.5)');
      cg.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = cg;
      ctx.beginPath();
      ctx.arc(cx2, cy2, cr, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  };

  const drawGlowRim = (mx2: number, my2: number, R2: number, a2: number) => {
    const rg = ctx.createRadialGradient(mx2, my2, R2 * 0.78, mx2, my2, R2 * 1.12);
    rg.addColorStop(0, 'rgba(240,246,255,0)');
    rg.addColorStop(0.72, 'rgba(240,246,255,' + (0.5 * a2).toFixed(3) + ')');
    rg.addColorStop(1, 'rgba(240,246,255,0)');
    ctx.fillStyle = rg;
    ctx.beginPath();
    ctx.arc(mx2, my2, R2 * 1.12, 0, Math.PI * 2);
    ctx.fill();
  };

  // --- bottom re-merge: past convergence the three beings fold back together ---
  const mg = ease((S - (cT + 100)) / 340);
  const cx3 = 0.5 * w;
  const cy3 = 0.81 * h;
  if (mg > 0) {
    // inspiral: orbit tighter and faster as they fall together
    const sw2 = mg * mg * 4.6;
    // pre-impact vibration: ramps up, cuts off at impact
    const vib = Math.pow(Math.min(1, mg / 0.68), 3) * Math.max(0, 1 - Math.max(0, (mg - 0.68) / 0.06));
    pts.forEach((p, i) => {
      const dx = p.x - cx3;
      const dy = p.y - cy3;
      const rad = Math.hypot(dx, dy) * (1 - ease(mg));
      const an3 = Math.atan2(dy, dx) + sw2;
      p.x = cx3 + Math.cos(an3) * rad + Math.sin(T * (26 + mg * 34 + i * 7)) * 10 * vib;
      p.y = cy3 + Math.sin(an3) * rad + Math.cos(T * (21 + mg * 28 + i * 9)) * 4 * vib;
      p.r = lerp(p.r, 16 * k, mg);
      p.fo *= 1 - mg;
    });
  }
  const mgF = ease(Math.min(1, mg * 1.45)); // orbs/streams survive until just before impact

  // --- hero intro: one breathing intelligence orb that splits on scroll ---
  const sp = ease(S / 260);
  const mx = cx(compact ? 0.5 : 0.72);
  const my = (compact ? 0.24 : 0.4) * h;
  if (sp < 1) {
    pts.forEach((p) => {
      p.x = lerp(mx, p.x, sp);
      p.y = lerp(my, p.y, sp);
      p.r = lerp(12 * k, p.r, sp);
    });
    const a = 1 - ease(Math.min(1, sp * 2.2)); // cloud vanishes fast in the bang
    const R = ((compact ? 84 : 104) + Math.sin(T * 0.9) * 4) * (1 + sp * 0.2) * k;
    // pre-bang rumble
    const rum = sp > 0 ? Math.max(0, 1 - Math.abs(sp - 0.12) / 0.24) : 0;
    ctx.save();
    ctx.translate(Math.sin(T * 43) * 3.2 * rum, Math.cos(T * 51) * 3.2 * rum);
    const hg = ctx.createRadialGradient(mx, my, R * 0.4, mx, my, R * 2.3);
    hg.addColorStop(0, 'rgba(130,175,255,' + (0.16 * a).toFixed(3) + ')');
    hg.addColorStop(1, 'rgba(130,175,255,0)');
    ctx.fillStyle = hg;
    ctx.beginPath();
    ctx.arc(mx, my, R * 2.3, 0, Math.PI * 2);
    ctx.fill();
    drawCloudBody(mx, my, R, a);
    // three color energies inside — on scroll they travel out and become the beings
    ctx.globalCompositeOperation = 'lighter';
    ORBS.forEach((o, i) => {
      const an = T * 0.4 + i * 2.094;
      const ix = mx + Math.cos(an) * R * 0.36;
      const iy = my + Math.sin(an) * R * 0.36;
      const bx = lerp(ix, pts[i].x, sp);
      const by = lerp(iy, pts[i].y, sp);
      const br = lerp(R * 0.62, Math.max(30 * k, pts[i].r * 1.7), sp);
      const al = (0.55 - sp * 0.05) * ease(sp * 2);
      if (al > 0.012) {
        const bgr = ctx.createRadialGradient(bx, by, 0, bx, by, br);
        bgr.addColorStop(0, rgba(o.col, al));
        bgr.addColorStop(1, rgba(o.col, 0));
        ctx.fillStyle = bgr;
        ctx.beginPath();
        ctx.arc(bx, by, br, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    ctx.globalCompositeOperation = 'source-over';
    drawGlowRim(mx, my, R, a);
    ctx.restore(); // end rumble offset
    // big bang: flash, shockwave, smoke
    if (sp > 0.02) {
      const bang = Math.max(0, 1 - Math.abs(sp - 0.3) / 0.3);
      if (bang > 0) {
        const fg = ctx.createRadialGradient(mx, my, 0, mx, my, R * 3.2);
        fg.addColorStop(0, 'rgba(255,255,255,' + (bang * 0.85).toFixed(3) + ')');
        fg.addColorStop(0.4, 'rgba(190,200,255,' + (bang * 0.35).toFixed(3) + ')');
        fg.addColorStop(1, 'rgba(190,200,255,0)');
        ctx.fillStyle = fg;
        ctx.beginPath();
        ctx.arc(mx, my, R * 3.2, 0, Math.PI * 2);
        ctx.fill();
      }
      if (sp > 0.12) {
        const sw = ease((sp - 0.12) / 0.88);
        ctx.beginPath();
        ctx.arc(mx, my, R + sw * w * 0.34, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(225,232,252,' + ((1 - sw) * 0.5).toFixed(3) + ')';
        ctx.lineWidth = 2.5 * (1 - sw) + 0.5;
        ctx.stroke();
      }
      if (sp > 0.06) {
        // ethereal smoke: soft puffs billowing outward, growing and thinning
        const fl = ease((sp - 0.06) / 0.94);
        ctx.globalCompositeOperation = 'lighter';
        for (let kk = 0; kk < 18; kk++) {
          const anK = (kk * 2.399) % 6.283;
          const spd = 0.5 + (((kk * 29) % 17) / 17) * 0.9;
          const dist = R * 0.4 + Math.pow(fl, 0.6) * w * 0.28 * spd;
          const alK = (1 - fl) * (0.3 - (kk % 3) * 0.055);
          if (alK < 0.015) continue;
          const px4 = mx + Math.cos(anK) * dist;
          const py4 = my + Math.sin(anK) * dist * 0.85 - fl * 26; // smoke drifts up
          const sz = (R * 0.26 + (kk % 4) * 9) * (0.5 + fl * 1.7);
          const colK = kk % 3 === 0 ? ORBS[((kk / 3) | 0) % 3].col : ([216, 222, 246] as const);
          const sg2 = ctx.createRadialGradient(px4, py4, 0, px4, py4, sz);
          sg2.addColorStop(0, rgba(colK, alK));
          sg2.addColorStop(0.55, rgba(colK, alK * 0.45));
          sg2.addColorStop(1, rgba(colK, 0));
          ctx.fillStyle = sg2;
          ctx.beginPath();
          ctx.arc(px4, py4, sz, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalCompositeOperation = 'source-over';
      }
    }
  }

  // --- pipeline edges: what actually flows between the beings ---
  let connA = 0.08;
  if (S < 650) connA = (0.3 - (S / 650) * 0.22) * sp;
  if (S > cT - 250) connA = Math.min(0.4, 0.08 + ((S - (cT - 250)) / 330) * 0.32);
  connA *= 1 - mgF;
  // Bright skies (day/golden hour) wash the streams out — scale opacity with
  // sky luminance, back the strokes with a dark halo, and blend the stream
  // colors toward dark ink so they read near-black against daylight while
  // staying pastel at night.
  const skyLum = (0.2126 * P[3] + 0.7152 * P[4] + 0.0722 * P[5]) / 255;
  connA *= 1 + skyLum * 2.4;
  const edgeShadow = skyLum > 0.3;
  const INK = [14, 20, 40];
  const inkMix = Math.min(1, Math.max(0, (skyLum - 0.25) / 0.45)) * 0.85;
  const edgeCol = (from: readonly number[], to: readonly number[], p: number) =>
    from.map((v, q) => {
      const m = v + (to[q] - v) * p;
      return (m + (INK[q] - m) * inkMix) | 0;
    });
  // TTS→ASR: audio waves · ASR→SLM: text · SLM→TTS: text
  const EDGES = [
    { f: 1, t: 0, kind: 'audio' },
    { f: 0, t: 2, kind: 'text' },
    { f: 2, t: 1, kind: 'text' },
  ] as const;
  const SENTS = ['', labels.sentUser + '   ', labels.sentAssistant + '   '];
  EDGES.forEach((e2, ei) => {
    const A2 = pts[e2.f];
    const B2 = pts[e2.t];
    const pa = Math.min(1, connA * 3);
    if (pa < 0.02) return;
    const fc = ORBS[e2.f].col;
    const tc = ORBS[e2.t].col;
    const ang = Math.atan2(B2.y - A2.y, B2.x - A2.x);
    const L2 = Math.hypot(B2.x - A2.x, B2.y - A2.y) || 1;
    const ux = (B2.x - A2.x) / L2;
    const uy = (B2.y - A2.y) / L2;
    ctx.save();
    if (edgeShadow) {
      ctx.shadowColor = 'rgba(10,14,30,0.85)';
      ctx.shadowBlur = 4;
    }
    if (e2.kind === 'audio') {
      // continuous waveform flowing along the whole line, source → target
      ctx.translate(A2.x, A2.y);
      ctx.rotate(ang);
      ctx.lineWidth = 1.5;
      ctx.lineCap = 'round';
      for (let d = 10; d < L2 - 10; d += 4.5) {
        const p = d / L2;
        const col = edgeCol(fc, tc, p);
        const aEnd = pa * Math.min(1, p * 8, (1 - p) * 8);
        const hh = 3 + 9 * Math.abs(Math.sin(d * 0.09 - T * 5.5)) * (0.55 + 0.45 * Math.sin(d * 0.023 + T * 1.7));
        ctx.strokeStyle = rgba(col, aEnd);
        ctx.beginPath();
        ctx.moveTo(d, -hh / 2);
        ctx.lineTo(d, hh / 2);
        ctx.stroke();
      }
      ctx.lineCap = 'butt';
    } else {
      const sent = SENTS[ei];
      // Per-character rendering breaks cursive/combining scripts (Arabic,
      // Devanagari, Thai, ...) — those locales get flowing dot particles.
      const complexScript = /[\u0590-\u08FF\u0900-\u0DFF\u0E00-\u0E7F\u1780-\u17FF]/.test(sent);
      ctx.font = '12.5px "IBM Plex Mono", monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const step = 9.5;
      const n2 = Math.floor(L2 / step);
      for (let i2 = 0; i2 < n2; i2++) {
        const d = (i2 * step + T * 26) % L2;
        const p = d / L2;
        // keep the sentence reading left-to-right on screen regardless of edge direction
        const ch = sent[B2.x < A2.x ? sent.length - 1 - (i2 % sent.length) : i2 % sent.length];
        if (ch === ' ') continue;
        const col = edgeCol(fc, tc, p);
        const aEnd = pa * Math.min(1, p * 8, (1 - p) * 8);
        if (complexScript) {
          ctx.fillStyle = rgba(col, aEnd);
          ctx.beginPath();
          ctx.arc(A2.x + ux * d, A2.y + uy * d, 1.7, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillStyle = rgba(col, aEnd);
          ctx.fillText(ch, A2.x + ux * d, A2.y + uy * d);
        }
      }
      ctx.textBaseline = 'alphabetic';
    }
    ctx.restore();
  });

  // --- the three beings ---
  const orbLabels = [labels.asr, labels.tts, labels.slm];
  ORBS.forEach((o, i) => {
    const p = pts[i];
    const cl = o.col;
    const r = p.r;
    if (sp < 0.02) return; // fully merged: only the intelligence sphere shows
    ctx.save();
    ctx.globalAlpha = Math.min(1, sp) * (1 - mgF);
    const gl = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r * 1.7);
    gl.addColorStop(0, rgba(cl, 0.5));
    gl.addColorStop(0.4, rgba(cl, 0.16));
    gl.addColorStop(1, rgba(cl, 0));
    ctx.fillStyle = gl;
    ctx.beginPath();
    ctx.arc(p.x, p.y, r * 1.7, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(p.x, p.y, r * 0.42, 0, Math.PI * 2);
    ctx.fillStyle =
      'rgba(' + Math.min(255, cl[0] + 80) + ',' + Math.min(255, cl[1] + 60) + ',' + Math.min(255, cl[2] + 40) + ',0.9)';
    ctx.fill();
    ctx.lineWidth = 1.2;
    if (o.kind === 0) {
      // listener: inward rings
      for (let kk = 0; kk < 3; kk++) {
        const ph = (T * 0.45 + kk / 3) % 1;
        const rr = r * (1.55 - ph * 1.0);
        ctx.beginPath();
        ctx.arc(p.x, p.y, rr, 0, Math.PI * 2);
        ctx.strokeStyle = rgba(cl, ph * 0.5);
        ctx.stroke();
      }
    } else if (o.kind === 1) {
      // voice: outgoing rings
      for (let kk = 0; kk < 3; kk++) {
        const ph = (T * 0.45 + kk / 3) % 1;
        const rr = r * (0.55 + ph * 1.05);
        ctx.beginPath();
        ctx.arc(p.x, p.y, rr, 0, Math.PI * 2);
        ctx.strokeStyle = rgba(cl, (1 - ph) * 0.5);
        ctx.stroke();
      }
    } else {
      // mind: rotating neural web
      const n = 7;
      const nds: number[][] = [];
      for (let kk = 0; kk < n; kk++) {
        const an = T * 0.25 + (kk / n) * Math.PI * 2;
        nds.push([p.x + Math.cos(an) * r * 0.75, p.y + Math.sin(an) * r * 0.62]);
      }
      ctx.strokeStyle = rgba(cl, 0.4);
      ctx.lineWidth = 0.8;
      for (let kk = 0; kk < n; kk++) {
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(nds[kk][0], nds[kk][1]);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(nds[kk][0], nds[kk][1]);
        ctx.lineTo(nds[(kk + 2) % n][0], nds[(kk + 2) % n][1]);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(nds[kk][0], nds[kk][1], 2.2, 0, Math.PI * 2);
        ctx.fillStyle = rgba(cl, 0.85);
        ctx.fill();
      }
    }
    // hero labels (only once the split has happened)
    const la = ease(1 - S / 480) * sp;
    if (la > 0.02) {
      ctx.font = '11px "IBM Plex Mono", monospace';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(230,238,250,' + (la * 0.75).toFixed(3) + ')';
      ctx.fillText(orbLabels[i], p.x, p.y + r + 24 * k);
    }
    ctx.restore();
  });

  // --- merged-orb caption ---
  if (sp < 0.95) {
    ctx.font = '11px "IBM Plex Mono", monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(230,238,250,' + ((1 - sp) * 0.8).toFixed(3) + ')';
    ctx.fillText(labels.intelligence, mx, my + (compact ? 128 : 158) * k);
  }

  // --- convergence halo (fades as the cloud re-forms) ---
  if (S > cT - 60) {
    const ca = ease((S - (cT - 60)) / 200) * (1 - mg);
    ctx.beginPath();
    ctx.arc(w * 0.5, h * 0.81, (76 + Math.sin(T * 1.2) * 6) * k, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(235,242,252,' + (ca * 0.4).toFixed(3) + ')';
    ctx.lineWidth = 1.4;
    ctx.stroke();
  }

  // --- collision: flash + gravitational ripples, then the cloud re-forms ---
  if (mg > 0.01) {
    const fl2 = Math.max(0, 1 - Math.abs(mg - 0.7) / 0.16);
    if (fl2 > 0) {
      const fr = 300 * k;
      const fg2 = ctx.createRadialGradient(cx3, cy3, 0, cx3, cy3, fr);
      fg2.addColorStop(0, 'rgba(255,255,255,' + (fl2 * 0.9).toFixed(3) + ')');
      fg2.addColorStop(0.35, 'rgba(185,205,255,' + (fl2 * 0.4).toFixed(3) + ')');
      fg2.addColorStop(1, 'rgba(185,205,255,0)');
      ctx.fillStyle = fg2;
      ctx.beginPath();
      ctx.arc(cx3, cy3, fr, 0, Math.PI * 2);
      ctx.fill();
    }
    if (mg > 0.62) {
      // expanding smoky shells — soft gradient rings, no hard lines
      const rp = (mg - 0.62) / 0.38;
      ctx.globalCompositeOperation = 'lighter';
      for (let kk = 0; kk < 3; kk++) {
        const pr = rp - kk * 0.17;
        if (pr <= 0 || pr >= 1) continue;
        const rr3 = 26 + pr * w * 0.26;
        const th3 = (34 + pr * 70) * k;
        const al3 = (1 - pr) * 0.26;
        const sh = ctx.createRadialGradient(cx3, cy3, Math.max(0, rr3 - th3), cx3, cy3, rr3 + th3);
        sh.addColorStop(0, 'rgba(200,212,250,0)');
        sh.addColorStop(0.5, 'rgba(208,218,250,' + al3.toFixed(3) + ')');
        sh.addColorStop(1, 'rgba(208,218,250,0)');
        ctx.fillStyle = sh;
        ctx.beginPath();
        ctx.arc(cx3, cy3, rr3 + th3, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = 'source-over';
    }
    const cA = ease((mg - 0.72) / 0.28);
    if (mg > 0.7 && cA < 1) {
      // lingering aftermath smoke the cloud condenses out of
      ctx.globalCompositeOperation = 'lighter';
      for (let kk = 0; kk < 9; kk++) {
        const an4 = kk * 0.698 + T * 0.22 * (kk % 2 ? 1 : -1);
        const rad4 = (26 + (1 - cA) * 58 + (kk % 3) * 14) * k;
        const px5 = cx3 + Math.cos(an4) * rad4;
        const py5 = cy3 + Math.sin(an4) * rad4 * 0.8;
        const al4 = (1 - cA) * (0.24 - (kk % 3) * 0.05);
        if (al4 < 0.015) continue;
        const sz4 = (34 + (kk % 4) * 12) * k;
        const colK4 = kk % 3 === 0 ? ORBS[((kk / 3) | 0) % 3].col : ([214, 220, 246] as const);
        const sg4 = ctx.createRadialGradient(px5, py5, 0, px5, py5, sz4);
        sg4.addColorStop(0, rgba(colK4, al4));
        sg4.addColorStop(1, rgba(colK4, 0));
        ctx.fillStyle = sg4;
        ctx.beginPath();
        ctx.arc(px5, py5, sz4, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = 'source-over';
    }
    if (cA > 0.01) {
      // Past the convergence beat the re-formed cloud drifts to the screen
      // edge, sinks toward the horizon and dims, so it never sits behind the
      // centered content of the sections that follow (Partnership, Languages,
      // Governance). Keyed to the moment the convergence text unpins so the
      // orb is parked before the next section's header scrolls in. Compact
      // viewports have no free edge, so there it sinks mostly below the hills
      // and dims harder instead.
      const post = ease((S - (cT + (f.anchors.conv?.pin ?? 0))) / 350);
      const edgeX = compact ? cx3 : f.rtl ? w * 0.1 : w * 0.9;
      const edgeDim = compact ? 0.6 : w < 1100 ? 0.5 : 0.3;
      const mxc = lerp(cx3, edgeX, post);
      const myc = lerp(cy3, (compact ? 0.97 : 0.9) * h, post);
      const cloudA = cA * (1 - post * edgeDim);
      const Rm = (86 + Math.sin(T * 0.9) * 4) * (0.8 + 0.2 * cA) * k * (1 - post * 0.3);
      const hg3 = ctx.createRadialGradient(mxc, myc, Rm * 0.4, mxc, myc, Rm * 2.3);
      hg3.addColorStop(0, 'rgba(130,175,255,' + (0.16 * cloudA).toFixed(3) + ')');
      hg3.addColorStop(1, 'rgba(130,175,255,0)');
      ctx.fillStyle = hg3;
      ctx.beginPath();
      ctx.arc(mxc, myc, Rm * 2.3, 0, Math.PI * 2);
      ctx.fill();
      drawCloudBody(mxc, myc, Rm, cloudA);
      drawGlowRim(mxc, myc, Rm, cloudA);
    }
  }
}
